import { useState, useEffect, useRef } from 'react'
import styled, { css } from 'styled-components'

import { useOverlayDrag } from '@/hooks/useOverlayDrag'
import { WidgetStep, Effect } from '@/types'
import { useDisplayTrigger } from '../../hooks/useDisplayTrigger'
import { useWidgetBreakpointSettings } from '../../hooks/useWidgetBreakpointSettings'
import { useFlowState } from '../../stores/useFlowState'
import { useSessionStore } from '@/stores/useSessionStore'
import { useWidgetStore } from '@/stores/useWidgetStore'
import {
  getIntroAnimation,
  getExitAnimation,
  getAttentionAnimation,
  WIDGET_ICON_DEFAULTS,
  type AttentionEffects,
} from '@/utils/animation-utils'
import { logger } from '@/utils'
import { track } from '@/utils/analytics'
import { DEFAULT_WIDGET_ICON } from '@/constants/default_images'
import { preloadDecodedImage } from '@/utils/preload-image'

const AnimatedContainer = styled.div<{
  $isExiting: boolean
  $introComplete: boolean
  $introEffect?: Effect
  $exitEffect?: Effect
  $attentionEffects?: AttentionEffects
  $borderRadius: string
  $promoteLayer: boolean
}>`
  display: flex;
  line-height: 0;
  border-radius: ${({ $borderRadius }) => $borderRadius};
  backface-visibility: hidden;
  ${({ $promoteLayer }) =>
    $promoteLayer
      ? css`
          will-change: transform, opacity;
        `
      : css`
          will-change: auto;
        `}
  ${({ $isExiting, $introComplete, $introEffect, $exitEffect, $attentionEffects }) => {
    if ($isExiting) return getExitAnimation($exitEffect, WIDGET_ICON_DEFAULTS)
    if ($introComplete) return getAttentionAnimation($attentionEffects)
    return getIntroAnimation($introEffect, WIDGET_ICON_DEFAULTS)
  }}
`

export default function WidgetIcon() {
  const { currentStep, setCurrentStep } = useFlowState()
  const { setFloatingWidgetOpen, floatingWidgetOpen, isClosing, closeFloatingWidget, demo } = useWidgetStore()
  const settingsLoaded = useSessionStore((state) => !!state.widgetSettings)
  const brandLogo = useSessionStore((state) => state.widgetSettings?.brandImages?.logo)
  const uid = useSessionStore((state) => state.currentUID)
  const { iconSettings } = useWidgetBreakpointSettings()
  const isEnabled = iconSettings?.enabled !== false
  const shouldShowByTrigger = useDisplayTrigger(iconSettings?.displaySettings, 'widget-icon', settingsLoaded)
  const shouldShow = isEnabled && shouldShowByTrigger
  const { handlePointerDown, shouldSuppressClick, dragStyle } = useOverlayDrag()

  const [isVisible, setIsVisible] = useState(false)
  const [isExiting, setIsExiting] = useState(false)
  const [introComplete, setIntroComplete] = useState(false)
  const [displaySrc, setDisplaySrc] = useState<string | null>(null)
  const hasPreloaded = useRef(false)

  const introEffect = iconSettings?.effects?.intro
  const exitEffect = iconSettings?.effects?.exit

  const attentionEffects: AttentionEffects = {
    glowEffect: iconSettings?.effects?.glowEffect,
    bounceEffect: iconSettings?.effects?.bounceEffect,
    peakingEffect: iconSettings?.effects?.peakingEffect,
    pulseEffect: iconSettings?.effects?.pulseEffect,
    shakeEffect: iconSettings?.effects?.shakeEffect,
    wobbleEffect: iconSettings?.effects?.wobbleEffect,
    spinEffect: iconSettings?.effects?.spinEffect,
    breatheEffect: iconSettings?.effects?.breatheEffect,
  }

  const hasAttentionEffects =
    attentionEffects.glowEffect?.enabled ||
    attentionEffects.bounceEffect?.enabled ||
    attentionEffects.peakingEffect?.enabled ||
    attentionEffects.pulseEffect?.enabled ||
    attentionEffects.shakeEffect?.enabled ||
    attentionEffects.wobbleEffect?.enabled ||
    attentionEffects.spinEffect?.enabled ||
    attentionEffects.breatheEffect?.enabled

  const imageURL = iconSettings?.mediaURL || brandLogo || `https://lab.alpineiq.com/avatar/${uid || ''}`
  const imageURLRef = useRef(imageURL)
  imageURLRef.current = imageURL

  useEffect(() => {
    let cancelled = false
    void preloadDecodedImage(imageURL, DEFAULT_WIDGET_ICON).then((src) => {
      if (!cancelled) setDisplaySrc(src)
    })
    return () => {
      cancelled = true
    }
  }, [imageURL])

  useEffect(() => {
    if (shouldShow && displaySrc) {
      if (isVisible && !isExiting) return
      const becameVisible = !isVisible
      setIsVisible(true)
      setIsExiting(false)
      setIntroComplete(false)
      if (becameVisible) {
        track('widget', 'widget_icon_shown', { imageURL: imageURLRef.current })
      }
      return
    }

    if (!shouldShow && isVisible && !isExiting) {
      setIsExiting(true)
      setIntroComplete(false)
      const exitDuration = exitEffect?.duration ?? WIDGET_ICON_DEFAULTS.exit.duration
      const timer = setTimeout(() => {
        setIsVisible(false)
      }, exitDuration)
      return () => clearTimeout(timer)
    }
  }, [shouldShow, displaySrc, isVisible, isExiting, exitEffect?.duration])

  useEffect(() => {
    if (isVisible && !isExiting && hasAttentionEffects) {
      const introDuration = introEffect?.duration ?? WIDGET_ICON_DEFAULTS.intro.duration
      const timer = setTimeout(() => setIntroComplete(true), introDuration)
      return () => clearTimeout(timer)
    }
  }, [isVisible, isExiting, hasAttentionEffects, introEffect?.duration])

  if (!isVisible || !displaySrc) return null

  const borderRadius = iconSettings?.borderRadius ?? {}
  const borderRadiusValue = `${borderRadius.topLeft ?? 33}px ${borderRadius.topRight ?? 33}px ${borderRadius.bottomRight ?? 33}px ${borderRadius.bottomLeft ?? 33}px`
  const iconHeight = iconSettings?.height || 48
  const iconWidth = iconSettings?.width || 48

  return (
    <AnimatedContainer
      $isExiting={isExiting}
      $introComplete={introComplete}
      $introEffect={introEffect}
      $exitEffect={exitEffect}
      $attentionEffects={attentionEffects}
      $borderRadius={borderRadiusValue}
      $promoteLayer={!introComplete || !!hasAttentionEffects || isExiting}
      style={{ pointerEvents: 'auto', ...dragStyle }}
      onPointerDown={handlePointerDown}
      onMouseEnter={() => {
        if (hasPreloaded.current) return
        hasPreloaded.current = true
        logger.log('WidgetIcon onMouseEnter', { demo })
        import('@/components/WidgetUI')
        logger.log('WidgetUI imported', { demo })
      }}
      onClick={() => {
        if (shouldSuppressClick() || demo || isClosing) return
        track('widget', 'widget_icon_clicked', { imageURL, action: 'open' })
        if (floatingWidgetOpen) {
          closeFloatingWidget()
        } else {
          setFloatingWidgetOpen(true)
          if (!currentStep) {
            setCurrentStep(WidgetStep.Welcome)
          }
        }
      }}
    >
      <img
        src={displaySrc}
        alt='Chat Widget Icon'
        height={iconHeight}
        width={iconWidth}
        decoding='async'
        fetchPriority='high'
        draggable={false}
        style={{
          display: 'block',
          objectFit: 'cover',
          borderRadius: borderRadiusValue,
          width: iconWidth,
          height: iconHeight,
        }}
      />
    </AnimatedContainer>
  )
}
